import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getReconciliation, ReconciliationItem, ReconciliationParams } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatTimestamp, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

type MatchStatus = 'MATCHED' | 'PARTIAL_MATCH' | 'UNMATCHED';
const STATUS_OPTIONS: { value: MatchStatus; label: string }[] = [
  { value: 'MATCHED', label: 'MATCHED' },
  { value: 'PARTIAL_MATCH', label: 'PARTIAL MATCH' },
  { value: 'UNMATCHED', label: 'UNMATCHED' },
];

const STATUS_BADGE_CLASS: Record<MatchStatus, string> = {
  MATCHED: 'badge badge-filled',
  PARTIAL_MATCH: 'badge badge-open',
  UNMATCHED: 'badge badge-failed',
};

export const ReconciliationView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [matchStatus, setMatchStatus] = useState<MatchStatus>('MATCHED');
  const [portfolioId, setPortfolioId] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_time', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: ReconciliationParams = {
    taxYear,
    matchStatus,
    portfolioId: portfolioId || undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'reconciliation', taxYear, matchStatus, portfolioId, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getReconciliation(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();

  const showMatchReason = matchStatus !== 'MATCHED';

  const columns = useMemo<ColumnDef<ReconciliationItem>[]>(() => {
    const base: ColumnDef<ReconciliationItem>[] = [
      {
        accessorKey: 'created_time',
        header: 'Created',
        cell: (info) => <span className="date-cell">{formatTimestamp(info.getValue() as string)}</span>,
      },
      {
        accessorKey: 'match_status',
        header: 'Status',
        cell: (info) => {
          const v = info.getValue() as MatchStatus;
          return <span className={STATUS_BADGE_CLASS[v]}>{v}</span>;
        },
      },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      {
        accessorKey: 'coinbase_order_id',
        header: 'Coinbase Order ID',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'system_client_order_id',
        header: 'System Client Order ID',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      { accessorKey: 'source_environment', header: 'Env' },
      { accessorKey: 'trade_group_type', header: 'Trade Group Type' },
      { accessorKey: 'trade_group_id', header: 'Group ID' },
    ];
    if (showMatchReason) {
      base.push({ accessorKey: 'match_reason', header: 'Match Reason' });
    }
    return base;
  }, [showMatchReason]);

  const filters: DataTableFilter[] = [
    {
      id: 'match_status',
      label: 'Status',
      value: matchStatus,
      options: STATUS_OPTIONS,
      // 'All' should map back to the default MATCHED rather than empty (server requires a value)
      onChange: (v) => {
        setMatchStatus((v || 'MATCHED') as MatchStatus);
        setPageIndex(0);
      },
    },
    {
      id: 'portfolio_id',
      label: 'Portfolio',
      value: portfolioId,
      options: portfolioOptions,
      onChange: onFilterChange(setPortfolioId),
    },
  ];

  return (
    <div aria-label="Reconciliation view">
      <DataTable<ReconciliationItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading reconciliation…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load reconciliation'}
        emptyMessage="No reconciliation rows for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default ReconciliationView;
